import { useEffect, useRef, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from './AuthContext';
import { toast } from 'react-toastify';

// Auto-logout after a period of inactivity (default: 30 minutes)
// Shows a 1-minute warning toast allowing the user to stay logged in.
export default function InactivityHandler({ timeout = 30 * 60 * 1000, warning = 60 * 1000 }) {
  const { logout } = useContext(AuthContext);
  const navigate = useNavigate();

  const warningTimerRef = useRef(null);
  const logoutTimerRef = useRef(null);
  const toastIdRef = useRef(null);

  const clearAllTimers = () => {
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }
  };

  const handleLogout = () => {
    try {
      logout();
      localStorage.setItem('showAutoLogoutToast', 'true');
    } catch (err) {
      // ignore
    }
    // dismiss warning toast if present
    if (toastIdRef.current) {
      toast.dismiss(toastIdRef.current);
      toastIdRef.current = null;
    }
    navigate('/');
  };

  const stayLoggedIn = () => {
    // user chose to stay logged in; reset timers and dismiss toast
    clearAllTimers();
    if (toastIdRef.current) {
      toast.dismiss(toastIdRef.current);
      toastIdRef.current = null;
    }
    scheduleTimers();
  };

  const showWarning = () => {
    if (toastIdRef.current) return; // already showing

    const content = (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div>You will be logged out in 1 minute due to inactivity.</div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={() => stayLoggedIn()}
            style={{ padding: '6px 10px', cursor: 'pointer' }}
          >
            Stay Logged In
          </button>
        </div>
      </div>
    );

    toastIdRef.current = toast.warn(content, { autoClose: false, closeOnClick: false });

    // start logout timer for the remaining warning duration
    logoutTimerRef.current = setTimeout(handleLogout, warning);
  };

  const scheduleTimers = () => {
    clearAllTimers();
    // time until showing warning is timeout - warning
    const timeUntilWarning = Math.max(0, timeout - warning);
    warningTimerRef.current = setTimeout(showWarning, timeUntilWarning);
  };

  const resetTimerOnActivity = () => {
    // dismiss any warning and restart timers
    if (toastIdRef.current) {
      toast.dismiss(toastIdRef.current);
      toastIdRef.current = null;
    }
    scheduleTimers();
  };

  useEffect(() => {
    const events = ['mousemove', 'mousedown', 'keypress', 'touchstart', 'scroll'];
    events.forEach((ev) => window.addEventListener(ev, resetTimerOnActivity));

    scheduleTimers();

    return () => {
      clearAllTimers();
      events.forEach((ev) => window.removeEventListener(ev, resetTimerOnActivity));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeout, warning]); // recreate timers if timeout/warning props change

  return null;
}
